/**
 * jaiio-search — socle du moteur de recherche maison (remplacement USF/Sobooster),
 * chargé sur TOUTES les pages par `layout/theme.liquid`.
 *
 * - Panneau de recherche instantanée de l'en-tête (gaté par
 *   jaiioSearchConfig.enable_instant_search).
 * - API publique `window.jaiioSearch` (on/getFilters/getState/getFacets ; les
 *   actions d'écriture ne deviennent réelles que sur une page de résultats).
 * - Client de l'API interne via l'app proxy Shopify : /apps/jaiio-api/search/*
 *   (cf. API_CONTRACT.md).
 *
 * La grille, les facettes, le tri, le chargement progressif et le zéro résultat
 * vivent dans `assets/jaiio-search-page.js`, que seules les sections qui rendent
 * `[data-jaiio-search]` chargent : ce code ne sert à rien sur l'accueil ou une
 * fiche (T058, poids du socle). Il s'appuie sur `window.jaiioSearch._core`.
 *
 * Vanilla JS, aucune dépendance.
 */
(function () {
  'use strict';

  var config = window.jaiioSearchConfig;
  if (!config) return;

  /* ──────────────────────────────────────────────────────────────────────────
   * Constantes front (ordre, types et labels des 13 facettes — contrat de parité)
   *
   * Les libellés viennent des locales : ce fichier ne sait pas lire `fr.json`,
   * c'est `snippets/jaiio-search-config.liquid` qui les injecte dans
   * `config.text.facets` — rendu globalement par `layout/theme.liquid`.
   * ────────────────────────────────────────────────────────────────────────── */

  var T = (config.text && config.text.facets) || {};
  var T_LABEL = T.label || {};

  var FACETS = [
    { key: 'univers',             label: T_LABEL.univers,            type: 'list' },
    { key: 'categorie',           label: T_LABEL.categorie,          type: 'list' },
    { key: 'sous_categorie',      label: T_LABEL.sous_categorie,     type: 'list' },
    { key: 'sous_sous_categorie', label: T_LABEL.sous_sous_categorie, type: 'list' },
    { key: 'marque',              label: T_LABEL.marque,             type: 'list', searchable: true },
    { key: 'taille',              label: T_LABEL.taille,             type: 'box' },
    { key: 'pointure',            label: T_LABEL.pointure,           type: 'box' },
    { key: 'couleur',             label: T_LABEL.couleur,            type: 'swatch' },
    { key: 'prix',                label: T_LABEL.prix,               type: 'price' },
    { key: 'etat',                label: T_LABEL.etat,               type: 'list' },
    { key: 'matiere',             label: T_LABEL.matiere,            type: 'list' },
    { key: 'model',               label: T_LABEL.model,              type: 'list' },
    { key: 'tags',                label: T_LABEL.tags,               type: 'list' },
    { key: 'prix_doux',           label: T_LABEL.prix_doux,          type: 'single',
      valueLabels: { '10': '−10 %', '20': '−20 %', '30': '−30 %' } }
  ];

  // Clés de filtres discrets (hors prix) telles qu'attendues par l'API.
  var FILTER_KEYS = FACETS.filter(function (f) { return f.type !== 'price'; })
    .map(function (f) { return f.key; });

  var PAGE_SIZE = 60;
  var FETCH_TIMEOUT = 2500;     // suggestions : ne pas bloquer la saisie
  var PRODUCTS_TIMEOUT = 10000; // la grille reste disponible sur connexion lente

  /* ──────────────────────────────────────────────────────────────────────────
   * Utils
   * ────────────────────────────────────────────────────────────────────────── */

  function esc(value) {
    return String(value == null ? '' : value)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  function debounce(fn, delay) {
    var t;
    return function () {
      var args = arguments, self = this;
      clearTimeout(t);
      t = setTimeout(function () { fn.apply(self, args); }, delay);
    };
  }

  function toNumber(value) {
    if (value == null || value === '') return null;
    var n = parseFloat(String(value).replace(',', '.'));
    return isNaN(n) ? null : n;
  }

  // Remplace les jetons %nom% d'une chaîne de locale passée par la config.
  // Pourcents et non accolades : une accolade simple dans un `{{ … }}` fait échouer
  // le parseur Liquid de Shopify à l'enregistrement du snippet de configuration.
  function fill(template, values) {
    return String(template == null ? '' : template).replace(/%(\w+)%/g, function (match, key) {
      return Object.prototype.hasOwnProperty.call(values, key) ? String(values[key]) : match;
    });
  }

  // « 1 234 » et non « 1234 » : les totaux de collection dépassent souvent le millier.
  function formatCount(n) {
    try { return Number(n).toLocaleString(document.documentElement.lang || 'fr'); } catch (e) { return String(n); }
  }

  // Normalisation accents/casse pour la recherche interne de la facette Créateurs.
  function normalize(str) {
    return String(str).toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  }

  // Palier « Réduction » du produit — metafield custom.prix_en_baisse, valant 10, 20 ou 30
  // (décimal : "30.0"). Ce n'est PAS la remise réelle : un article à 55€ barré 60€ (−8 %)
  // peut porter le palier 30.
  //
  // Comme snippets/product-item.liquid, badge et prix barré n'apparaissent qu'à partir de 30
  // et seulement si le prix est réellement inférieur au prix comparé. Renvoie le pourcentage
  // arrondi à afficher, ou null.
  //
  // ⚠️ /search/products et /search/suggest ne renvoient pas encore `prix_en_baisse` : la
  // fonction rend null partout, le badge reste donc masqué jusqu'à la mise à jour de l'API.
  // `show_prix_doux` ne peut pas s'y substituer — il est vrai pour les trois paliers.
  function priceDropPct(item) {
    var pct = toNumber(item.prix_en_baisse);
    if (pct == null || pct < 30) return null;
    if (item.compare_at_price == null || !(item.price < item.compare_at_price)) return null;
    return Math.round(pct);
  }

  function imageWithWidth(url, width) {
    if (!url) return '';
    var sep = url.indexOf('?') === -1 ? '?' : '&';
    return url + sep + 'width=' + width;
  }

  function dataLayerPush(payload) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(payload);
  }

  /* ──────────────────────────────────────────────────────────────────────────
   * État — source de vérité : l'URL
   * ────────────────────────────────────────────────────────────────────────── */

  var state = {
    q: null,
    sort: config.default_sort,
    filters: {},        // { marque: ['Sézane'], taille: ['S','M'], ... }
    prixMin: null,
    prixMax: null,
    take: PAGE_SIZE     // nb d'items chargés (pour la restauration au back)
  };

  var results = {
    appliedSort: null,
    items: [],
    total: 0,
    facets: {},
    priceBounds: null,  // bornes du slider — figées tant qu'un filtre prix est actif
    loading: false
  };

  /* ──────────────────────────────────────────────────────────────────────────
   * Petit émetteur d'événements + API publique
   * ────────────────────────────────────────────────────────────────────────── */

  var listeners = {};
  function emit(event, payload) {
    (listeners[event] || []).forEach(function (cb) {
      try { cb(payload); } catch (e) { console.error('[jaiio-search]', e); }
    });
  }

  function getFilters() {
    // Clés canoniques du modèle SavedSearch (le back accepte aussi `tags`) — prix_doux exclu.
    var canonical = ['univers', 'categorie', 'sous_categorie', 'sous_sous_categorie',
      'marque', 'couleur', 'etat', 'matiere', 'pointure', 'taille', 'tags'];
    var context = config.context || {};
    var out = {};
    canonical.forEach(function (key) {
      var values = [];
      var ctx = context[key];
      if (ctx != null && ctx !== '') values = Array.isArray(ctx) ? ctx.slice() : [String(ctx)];
      var ui = state.filters[key];
      if (ui && ui.length) values = ui.slice(); // le choix UI écrase le contexte (raffinement volontaire)
      out[key] = values;
    });
    // Prix : UNIQUEMENT si la cliente a filtré le prix, et alors toujours avec les DEUX
    // bornes (borne manquante substituée par la borne courante du slider — parité USF
    // ":min :max" ; le validateur back exige deux numbers). Sinon null : les bornes
    // implicites de la collection partent déjà via le collectionContext du save-search.
    var prix = null;
    if (state.prixMin != null || state.prixMax != null) {
      var bounds = results.priceBounds || {};
      var min = state.prixMin != null ? state.prixMin : toNumber(bounds.min);
      var max = state.prixMax != null ? state.prixMax : toNumber(bounds.max);
      if (min != null && max != null) prix = { min: min, max: max };
    }
    out.prix = prix;
    return out;
  }

  // Instantané lisible de l'état — destiné à une facet-bar externe (thème).
  // Clés `filters` = FILTER_KEYS ; `prix` null si aucun filtre prix actif.
  function getState() {
    var filters = {};
    FILTER_KEYS.forEach(function (key) {
      filters[key] = (state.filters[key] || []).slice();
    });
    var prix = null;
    if (state.prixMin != null || state.prixMax != null) {
      prix = { min: state.prixMin, max: state.prixMax };
    }
    var bounds = results.priceBounds;
    return {
      q: state.q,
      sort: state.sort,
      filters: filters,
      prix: prix,
      priceBounds: bounds ? { min: bounds.min, max: bounds.max } : null,
      total: results.total,
      loading: results.loading
    };
  }

  var selectionTags = (config.selection_tags || []).map(function (tag) { return String(tag).trim(); }).filter(Boolean);

  // La liste marchande limite les nouvelles options, jamais les filtres d'un lien
  // partagé : les rendus réinsèrent ensuite les valeurs actives pour les retirer.
  function isFacetValueVisible(key, label) {
    return key !== 'tags' || selectionTags.indexOf(label) !== -1;
  }

  // Distribution + compteurs de la dernière réponse API — { key: { label: count } }.
  // Copie défensive : la facet-bar ne doit pas muter l'état interne.
  function getFacets() {
    var out = {};
    Object.keys(results.facets).forEach(function (key) {
      var dist = results.facets[key], copy = {};
      Object.keys(dist).forEach(function (label) { copy[label] = dist[label]; });
      out[key] = copy;
    });
    return out;
  }

  // Les actions d'écriture ne sont réelles qu'une fois la page de recherche
  // initialisée (elles bouclent sur loadFresh, closure d'initSearchPage, dans
  // assets/jaiio-search-page.js, qui les remplace ici).
  function notReady() {
    console.warn('[jaiio-search] action ignorée : composant non initialisé (hors page de recherche).');
    return window.jaiioSearch;
  }

  window.jaiioSearch = {
    getFilters: getFilters,
    getState: getState,
    getFacets: getFacets,
    setFilter: notReady,
    setFilters: notReady,
    toggleFilter: notReady,
    setPrice: notReady,
    setSort: notReady,
    clear: notReady,
    on: function (event, cb) {
      (listeners[event] = listeners[event] || []).push(cb);
      return window.jaiioSearch;
    }
  };

  // Pièces du socle dont `assets/jaiio-search-page.js` a besoin : le fichier de la
  // page de résultats est chargé à part (sections moteur seulement) et partage
  // ainsi le même état, le même émetteur et le même client d'API — une seule
  // source de vérité, pas de copie. Interne : aucun autre script ne doit s'en servir.
  window.jaiioSearch._core = {
    config: config,
    FACETS: FACETS,
    FILTER_KEYS: FILTER_KEYS,
    PAGE_SIZE: PAGE_SIZE,
    state: state,
    results: results,
    emit: emit,
    getFilters: getFilters,
    getState: getState,
    apiFetch: apiFetch,
    isFacetValueVisible: isFacetValueVisible,
    esc: esc,
    toNumber: toNumber,
    fill: fill,
    formatCount: formatCount,
    normalize: normalize,
    imageWithWidth: imageWithWidth,
    dataLayerPush: dataLayerPush,
    renderPrices: renderPrices,
    renderAttributes: renderAttributes,
    cardAttributesText: cardAttributesText
  };

  /* ──────────────────────────────────────────────────────────────────────────
   * Client API
   * ────────────────────────────────────────────────────────────────────────── */

  function apiFetch(path, params, options) {
    var controller = new AbortController();
    var signal = options && options.signal;
    function abort() { controller.abort(); }
    if (signal) {
      if (signal.aborted) abort();
      else signal.addEventListener('abort', abort, { once: true });
    }
    var timer = setTimeout(function () { controller.abort(); }, path === '/search/products' ? PRODUCTS_TIMEOUT : FETCH_TIMEOUT);
    var qs = params ? '?' + params.toString() : '';
    return fetch(config.api_base + path + qs, {
      signal: controller.signal,
      credentials: 'same-origin',
      headers: { Accept: 'application/json' }
    }).then(function (res) {
      if (!res.ok) {
        var err = new Error('HTTP ' + res.status);
        err.status = res.status;
        err.retryAfter = res.headers && res.headers.get ? res.headers.get('Retry-After') : null;
        throw err;
      }
      return res.json();
    }).finally(function () {
      clearTimeout(timer);
      if (signal) signal.removeEventListener('abort', abort);
    });
  }

  /* ──────────────────────────────────────────────────────────────────────────
   * Prix et attributs des cartes — partagés par la carte de la grille
   * (`renderCard`, assets/jaiio-search-page.js) et la vignette du panneau.
   * ────────────────────────────────────────────────────────────────────────── */

  // Prix de carte en euros entiers : le back renvoie « 85,50€ », on coupe les
  // décimales sans arrondir, comme la division entière du snippet Liquid.
  function wholeMoney(formatted) {
    return String(formatted || '').replace(/[.,]\d{1,2}(?=\D*$)/, '');
  }

  // Montant d'un prix formaté (« 1.250,00€ » → 1250). Le back ne renvoie le
  // prix neuf que formaté.
  function parseMoney(formatted) {
    return toNumber(String(formatted || '').replace(/[^\d,]/g, ''));
  }

  // Décote face au neuf, tronquée comme dans snippets/product-item.liquid (neuf
  // coupé à l'euro, puis division entière). null si le neuf n'est pas plus cher.
  function retailOffPct(item) {
    var retail = Math.floor(parseMoney(item.retail_price_formatted) || 0);
    var price = toNumber(item.price);
    if (!retail || price == null) return null;
    // Le 1e-9 absorbe l'erreur des flottants (29,999… doit rester 30).
    var pct = Math.floor((retail - price) * 100 / retail + 1e-9);
    return pct > 0 ? pct : null;
  }

  // Prix courant, puis remise (barré + décote en corail) OU décote face au neuf.
  // Jamais les deux : deux références de prix sur la même carte se contredisent.
  // `compact` (vignettes du panneau instantané) : sans la ligne du prix neuf.
  function renderPrices(item, compact) {
    var dropPct = priceDropPct(item);
    var showDiscount = config.settings.show_discount && item.available &&
      dropPct != null && item.compare_at_price_formatted;

    var html = '<div class="product-item-meta__price-list-container pr"><div class="pr-main">' +
      '<span class="price' + (item.available ? '' : ' sold') + '">' +
      '<span class="visually-hidden">' + esc(config.text.sale_price) + '</span>' +
      esc(wholeMoney(item.price_formatted)) + '</span>';

    if (showDiscount) {
      html += '<span class="was">' +
        '<span class="visually-hidden">' + esc(config.text.regular_price) + '</span>' +
        esc(wholeMoney(item.compare_at_price_formatted)) + '</span>' +
        '<span class="off">-' + dropPct + '\u00a0%</span>';
    }
    html += '</div>';

    if (!compact && !showDiscount && item.retail_price_formatted) {
      var retailPct = retailOffPct(item);
      html += '<p class="neuf"><b>' + esc(config.text.retail_price) + '</b> ' +
        '<em>' + esc(wholeMoney(item.retail_price_formatted)) + '</em>' +
        (retailPct ? ' <span class="off">-' + retailPct + '\u00a0%</span>' : '') + '</p>';
    }
    return html + '</div>';
  }

  function cardAttributesText(item) {
    return item && item.card_attributes_version === 1 && typeof item.card_attributes === 'string' ? item.card_attributes : null;
  }

  // Ligne « Taille 38 · Coton · Très bon état ». `size_label` porte déjà son
  // préfixe (« Taille M », « Pointure 39 »). La matière et l'état ne sont pas
  // toujours renvoyés par /search/products : la ligne se réduit alors, et elle
  // reste rendue même vide pour que la grille du serveur et celle du JS aient
  // exactement le même nombre de lignes.
  function renderAttributes(item) {
    var complete = cardAttributesText(item);
    if (complete !== null) return '<div class="product-item-meta__attributes attrs">' + esc(complete) + '</div>';
    var parts = [];
    if (item.size_label) parts.push(item.size_label);
    if (item.matiere) parts.push(item.matiere);
    if (item.etat) parts.push(item.etat);
    return '<div class="product-item-meta__attributes attrs">' + esc(parts.join(' \u00b7 ')) + '</div>';
  }

  if (config.enable_instant_search) initInstantSearch();

  /* ──────────────────────────────────────────────────────────────────────────
   * Instant search header
   * — écoute les input[name=q] du header SANS les cloner ;
   * — seule recherche instantanée du thème : la chaîne predictive-search de Focal
   *   est retirée (T050) ;
   * — gaté par config.enable_instant_search, que le layout force à vrai.
   * ────────────────────────────────────────────────────────────────────────── */

  function initInstantSearch() {
    document.body.classList.add('jaiio-instant-search-enabled');

    // Recherches récentes retirées (Q30, T050) : l'historique que l'ancienne
    // version laissait dans le navigateur est effacé, il ne sert plus à rien.
    try { localStorage.removeItem('jaiioRecentSearches'); } catch (e) { /* stockage indisponible */ }

    var TXT = config.text;

    // Panneau non modal (recherche.dc.html, artboards « panneau ») : la page reste
    // lisible derrière un voile, le champ garde le focus et la frappe.
    var dropdown = document.createElement('div');
    dropdown.className = 'jaiio-is';
    dropdown.id = 'jaiio-is';
    dropdown.hidden = true;
    dropdown.setAttribute('role', 'dialog');
    dropdown.setAttribute('aria-label', TXT.suggestions);
    // Posé juste après la section de l'en-tête, pas en fin de <body> : l'ordre de
    // lecture (tabulation, balayage VoiceOver) va du champ au panneau sans
    // traverser la page.
    var headerSection = document.querySelector('store-header');
    headerSection = headerSection && headerSection.closest('.shopify-section');
    if (headerSection && headerSection.parentNode === document.body) headerSection.after(dropdown);
    else document.body.appendChild(dropdown);

    // Voile de la page, posé SOUS l'en-tête : le champ où l'on tape reste net.
    var scrim = document.createElement('div');
    scrim.className = 'jaiio-is-scrim';
    scrim.hidden = true;
    document.body.appendChild(scrim);

    var activeInput = null;
    var suggestToken = 0;
    var restoringInputFocus = false;

    var DROPDOWN_WIDTH = 980;   // largeur de l'artboard desktop
    var DROPDOWN_OFFSET = 12;   // écart sous le filet du champ
    var MOBILE_QUERY = window.matchMedia('(max-width: 999px)');

    // Rangée de recherche mobile de l'en-tête (T052) et la loupe qui la déplie.
    var mobileRow = document.getElementById('jh-msearch');
    var mobileTrigger = document.querySelector('[data-jaiio-search-open]');
    var persistentRow = !!mobileRow && mobileRow.hasAttribute('data-search-persistent');

    function rowOpen() {
      return !!mobileRow && !persistentRow && !mobileRow.hidden;
    }

    // Gouttière de page du socle (`--j-pad`, 20 px, 40 px dès 741 px) : le panneau
    // ne descend jamais dedans, même quand la pilule est proche du bord.
    function pageGutter() {
      var v = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--j-pad'));
      return isNaN(v) ? 16 : v;
    }

    function goToSearch(term) {
      if (!term) return;
      dataLayerPush({ event: 'jaiio_search', search_term: term, source: 'instant_search' });
      window.location.assign(searchUrl(term));
    }

    function searchUrl(term) {
      return config.routes.search_url + '?q=' + encodeURIComponent(term);
    }

    function setExpanded(value) {
      if (activeInput) activeInput.setAttribute('aria-expanded', value ? 'true' : 'false');
    }

    // Bas de l'en-tête dans la fenêtre : le voile, et en mobile le panneau,
    // commencent là — le champ où l'on tape reste net et à sa place.
    function headerBottom() {
      var header = (activeInput && activeInput.closest('store-header')) || document.querySelector('store-header');
      return header ? Math.max(0, header.getBoundingClientRect().bottom) : 0;
    }

    // Desktop : le panneau s'aligne sur le bord gauche de la PILULE (le form), pas
    // sur l'input nu, et s'étend vers la droite ; il est ramené dans les
    // gouttières quand la fenêtre est trop étroite pour ses 980 px.
    // Mobile (artboard M) : il occupe la fenêtre sous l'en-tête et défile seul.
    function position() {
      // Écouté au défilement sur toutes les pages : rien à lire ni à écrire tant
      // que ni le panneau ni la rangée mobile (qui montrent le voile) ne sont ouverts.
      if (scrim.hidden) return;
      scrim.style.top = headerBottom() + 'px';
      if (dropdown.hidden || !activeInput) return;
      var sheet = MOBILE_QUERY.matches;
      dropdown.classList.toggle('jaiio-is--sheet', sheet);
      if (sheet) {
        clearInlinePosition();
        dropdown.style.top = headerBottom() + 'px';
        return;
      }
      var anchor = activeInput.form || activeInput;
      var rect = anchor.getBoundingClientRect();
      var viewport = document.documentElement.clientWidth;
      var gutter = pageGutter();
      var width = Math.min(DROPDOWN_WIDTH, viewport - gutter * 2);
      var left = Math.max(gutter, Math.min(rect.left, viewport - width - gutter));
      var top = rect.bottom + DROPDOWN_OFFSET;
      dropdown.style.top = (top + window.scrollY) + 'px';
      dropdown.style.left = (left + window.scrollX) + 'px';
      dropdown.style.width = width + 'px';
      dropdown.style.maxHeight = Math.max(240, window.innerHeight - top - gutter) + 'px';
    }

    function clearInlinePosition() {
      dropdown.style.top = '';
      dropdown.style.left = '';
      dropdown.style.width = '';
      dropdown.style.maxHeight = '';
    }

    function show() {
      dropdown.hidden = false;
      scrim.hidden = false;
      setExpanded(true);
      position();
    }

    // Ferme le panneau. La rangée mobile, elle, reste ouverte (et la page voilée)
    // tant qu'on ne la ferme pas.
    function hide() {
      suggestToken++;
      dropdown.hidden = true;
      scrim.hidden = !rowOpen();
      clearInlinePosition();
      setExpanded(false);
    }

    // Rangée mobile : la loupe la déplie, le défilement de la page est verrouillé
    // (`jaiio-search-lock`, distinct du verrou des facettes), le focus va au champ.
    function openRow() {
      mobileRow.hidden = false;
      if (mobileTrigger) mobileTrigger.setAttribute('aria-expanded', 'true');
      document.documentElement.classList.add('jaiio-search-lock');
      scrim.hidden = false;
      position();
      var input = mobileRow.querySelector('input[name="q"]');
      activeInput = input;
      input.focus();
    }

    // Fermeture : panneau et rangée repliés, page déverrouillée, focus rendu à la loupe.
    function closeRow() {
      suggestToken++;
      mobileRow.hidden = true;
      hide();
      if (mobileTrigger) mobileTrigger.setAttribute('aria-expanded', 'false');
      document.documentElement.classList.remove('jaiio-search-lock');
      if (mobileTrigger) mobileTrigger.focus();
    }

    // Surbrillance de ce qui a été tapé : chaque mot de la requête, retrouvé sans
    // tenir compte des accents ni de la casse (« sez » trouve « Sézane »), passe en
    // 500. Le texte est échappé morceau par morceau, jamais injecté tel quel.
    function highlight(text, term) {
      text = String(text == null ? '' : text);
      var map = [];   // index dans la forme normalisée → index dans le texte d'origine
      var flat = '';
      for (var i = 0; i < text.length; i++) {
        var n = normalize(text[i]);
        for (var k = 0; k < n.length; k++) map.push(i);
        flat += n;
      }
      var marks = new Array(text.length);
      normalize(term).split(/\s+/).forEach(function (word) {
        if (!word) return;
        var from = flat.indexOf(word);
        while (from !== -1) {
          for (var j = from; j < from + word.length; j++) marks[map[j]] = true;
          from = flat.indexOf(word, from + word.length);
        }
      });
      var html = '', open = false;
      for (var c = 0; c < text.length; c++) {
        if (marks[c] && !open) { html += '<b>'; open = true; }
        if (!marks[c] && open) { html += '</b>'; open = false; }
        html += esc(text[c]);
      }
      return html + (open ? '</b>' : '');
    }

    // Un jeton `%nom%` de locale remplacé par du HTML déjà échappé.
    function fillHtml(template, token, html) {
      return esc(template).replace('%' + token + '%', html);
    }

    function plural(forms, count) {
      return fill(count === 1 ? forms.one : forms.other, { count: formatCount(count) });
    }

    var SEARCH_ICON = '<svg class="uic" viewBox="0 0 21 21" aria-hidden="true" focusable="false"><circle cx="9.5" cy="9.5" r="6.2"></circle><path d="M14.3 14.3 L19 19"></path></svg>';

    // Carte v4 réduite : photo, marque, titre, attributs, prix. Ni mention ni cœur —
    // et surtout pas les classes `product-item__image-wrapper` / `__aspect-ratio`,
    // sur lesquelles Swish poserait ses cœurs.
    function renderMiniCard(item) {
      return '<a class="pc4 jaiio-is__card" href="' + esc(item.url) + '" data-is-item>' +
        '<span class="gal">' +
        (item.image_url ? '<img class="product-item__primary-image" loading="lazy" src="' + esc(imageWithWidth(item.image_url, 300)) + '" alt="' + esc(item.image_alt || item.title) + '">' : '') +
        '</span>' +
        '<span class="product-item-meta meta">' +
        '<span class="brand">' + esc(item.brand || '') + '</span>' +
        '<span class="title">' + esc(item.title) + '</span>' +
        renderAttributes(item) +
        renderPrices(item, true) +
        '</span>' +
        '</a>';
    }

    function renderGroup(title, aside) {
      return '<p class="jaiio-is__grp"><span>' + esc(title) + '</span>' + (aside ? '<span>' + aside + '</span>' : '') + '</p>';
    }

    function renderSuggestResults(term, data) {
      var mobile = MOBILE_QUERY.matches;
      // Quatre pièces partout : une rangée de 4 en desktop, 2 × 2 en mobile. Deux
      // rangées de 3 (maquette) faisaient 855 px et défilaient sous 1440 × 900
      // (recette d'Antoine, 2026-09-28).
      var products = (data.products || []).slice(0, 4);
      var suggestions = (data.suggestions || []).slice(0, 6);
      var collections = (data.collections || []).slice(0, 4);
      // Q27 : les compteurs ne s'affichent que si l'API les donne vraiment.
      var total = typeof data.total === 'number' && data.total >= products.length ? data.total : null;

      if (!products.length && !suggestions.length && !collections.length) {
        return '<p class="jaiio-is__empty">' + fillHtml(TXT.no_matches_for, 'term', '<b>' + esc(term) + '</b>') + '</p>';
      }

      var side = '';
      if (suggestions.length) {
        side += renderGroup(TXT.suggestions) + suggestions.map(function (t) {
          return '<button type="button" class="jaiio-is__it" data-jaiio-is-term="' + esc(t) + '" data-is-item>' +
            SEARCH_ICON + '<span>' + highlight(t, term) + '</span></button>';
        }).join('');
      }
      if (collections.length) {
        if (side) side += '<span class="jaiio-is__sep" aria-hidden="true"></span>';
        side += renderGroup(TXT.collections) + collections.map(function (c) {
          var count = typeof c.count === 'number'
            ? '<span class="jaiio-is__n" aria-hidden="true">' + esc(formatCount(c.count)) + '</span>' +
              '<span class="visually-hidden">, ' + esc(plural(TXT.facets.pieces_count, c.count)) + '</span>'
            : '';
          return '<a class="jaiio-is__it" href="' + esc(c.url) + '" data-is-item><span>' + highlight(c.title, term) + '</span>' + count + '</a>';
        }).join('');
      }

      var main = '';
      if (products.length) {
        var all = total != null && mobile
          ? esc(plural(TXT.view_all_count, total))
          : fillHtml(TXT.view_all_results_for, 'term', '<b>' + esc(term) + '</b>');
        main = '<div class="jaiio-is__main">' +
          renderGroup(TXT.pieces, total != null ? esc(plural(TXT.matching, total)) : '') +
          '<div class="jaiio-is__prods">' + products.map(renderMiniCard).join('') + '</div>' +
          '<div class="jaiio-is__foot">' +
          '<a class="jaiio-is__all" href="' + esc(searchUrl(term)) + '" data-jaiio-is-term="' + esc(term) + '" data-is-item>' + all + '</a>' +
          '<span class="jaiio-is__kbd" aria-hidden="true"><kbd>⏎</kbd> ' + esc(TXT.validate_hint) + ' · <kbd>esc</kbd> ' + esc(TXT.close_hint) + '</span>' +
          '</div>' +
          '</div>';
      }

      var modifier = '';
      if (!side) modifier = ' jaiio-is__cols--main';
      else if (!main) modifier = ' jaiio-is__cols--side';

      return '<div class="jaiio-is__cols' + modifier + '">' +
        (side ? '<div class="jaiio-is__side">' + side + '</div>' : '') + main +
        '</div>';
    }

    // Réponses déjà reçues, le temps de la page : effacer une lettre puis la retaper
    // ne refait pas l'appel (l'API limite à 30 requêtes par minute et par IP). Les
    // échecs ne sont pas gardés.
    var suggestCache = new Map();
    var SUGGEST_CACHE_MAX = 30;

    var runSuggest = debounce(function (term, generation) {
      if (generation !== suggestToken) return;
      if (persistentRow && MOBILE_QUERY.matches && activeInput && mobileRow.contains(activeInput) && mobileRow.classList.contains('is-scroll-hidden')) return;
      var token = ++suggestToken;
      var cached = suggestCache.get(term);
      (cached ? Promise.resolve(cached) : apiFetch('/search/suggest', new URLSearchParams({ q: term })))
        .then(function (data) {
          if (!cached) {
            if (suggestCache.size >= SUGGEST_CACHE_MAX) suggestCache.delete(suggestCache.keys().next().value);
            suggestCache.set(term, data);
          }
          if (token !== suggestToken) return;
          dropdown.innerHTML = renderSuggestResults(term, data);
          show();
        })
        .catch(function () {
          // L'instant search est un confort : en cas d'échec (429, 5xx, délai), on
          // ferme le panneau plutôt que d'afficher des suggestions périmées.
          if (token === suggestToken) hide();
        });
    }, 180);

    function onInput(event) {
      activeInput = event.target;
      var term = activeInput.value.trim();
      // Moins de deux lettres, champ vide compris : aucun panneau.
      if (term.length < 2) {
        suggestToken++;
        hide();
        return;
      }
      runSuggest(term, ++suggestToken);
    }

    // Clavier : ↓ depuis le champ entre dans le panneau, ↑ ↓ passent d'une entrée à
    // l'autre, Échap ferme et rend le champ. Le panneau est rendu en fin de <body> :
    // sans ce relais, la tabulation n'y arriverait qu'après toute la page.
    function items() {
      return [].slice.call(dropdown.querySelectorAll('[data-is-item]'));
    }

    function onInputKeydown(event) {
      if (event.key === 'ArrowDown' && !dropdown.hidden && items().length) {
        event.preventDefault();
        items()[0].focus();
      }
    }

    dropdown.addEventListener('keydown', function (event) {
      var list = items();
      var index = list.indexOf(document.activeElement);
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        if (index === -1) return;
        var next = index + (event.key === 'ArrowDown' ? 1 : -1);
        if (next < 0) { if (activeInput) activeInput.focus(); return; }
        if (next < list.length) list[next].focus();
      }
    });

    function attachToInput(input) {
      if (input.hasAttribute('data-jaiio-is-attached')) return;
      input.setAttribute('data-jaiio-is-attached', '1');
      input.setAttribute('autocomplete', 'off');
      input.setAttribute('role', 'combobox');
      input.setAttribute('aria-haspopup', 'dialog');
      input.setAttribute('aria-controls', dropdown.id);
      input.setAttribute('aria-expanded', 'false');
      input.addEventListener('input', onInput);
      input.addEventListener('focus', function (event) {
        if (!restoringInputFocus) onInput(event);
      });
      input.addEventListener('keydown', onInputKeydown);
      var form = input.form;
      if (form && !form.hasAttribute('data-jaiio-is-attached')) {
        form.setAttribute('data-jaiio-is-attached', '1');
        form.addEventListener('submit', function (event) {
          event.preventDefault();
          goToSearch(input.value.trim());
        });
      }
    }

    document.querySelectorAll('input[name="q"]').forEach(attachToInput);

    if (mobileRow && mobileTrigger) {
      mobileTrigger.addEventListener('click', function (event) {
        event.preventDefault();
        if (rowOpen()) closeRow();
        else openRow();
      });
      var closeButton = mobileRow.querySelector('[data-jaiio-search-close]');
      if (closeButton) closeButton.addEventListener('click', closeRow);
      // Revenue en largeur desktop (rotation d'une tablette) : la rangée n'existe plus
      // à l'écran, le verrou ne doit pas lui survivre.
      MOBILE_QUERY.addEventListener('change', function (event) {
        if (!event.matches && rowOpen()) closeRow();
      });
    }

    // Champ permanent mobile : à l'arrivée il est disponible sans clavier,
    // voile ou verrou. Le défilement le replie et ferme les suggestions ; la
    // saisie reste dans le DOM pour être retrouvée au retour en haut.
    if (persistentRow) {
      var searchTicking = false, searchSettleUntil = 0;
      function setSearchCollapsed(collapsed) {
        if (mobileRow.classList.contains('is-scroll-hidden') === collapsed) return;
        if (collapsed) {
          if (mobileRow.contains(document.activeElement)) document.activeElement.blur();
          suggestToken++;
          hide();
        }
        mobileRow.classList.toggle('is-scroll-hidden', collapsed);
        mobileRow.inert = collapsed;
        if (collapsed) mobileRow.setAttribute('aria-hidden', 'true');
        else mobileRow.removeAttribute('aria-hidden');
        // Ne pas interpréter l'ancrage de scroll dû aux 32 px retirés comme
        // une nouvelle intention de défilement.
        searchSettleUntil = performance.now() + 320;
      }
      function updateSearchRow() {
        searchTicking = false;
        if (!MOBILE_QUERY.matches) {
          setSearchCollapsed(false);
          hide();
          return;
        }
        if (performance.now() < searchSettleUntil) return;
        var y = Math.max(0, window.scrollY);
        if (y <= 8) setSearchCollapsed(false);
        else if (y > 48) setSearchCollapsed(true);
      }
      function scheduleSearchRow() {
        if (searchTicking) return;
        searchTicking = true;
        requestAnimationFrame(updateSearchRow);
      }
      window.addEventListener('scroll', scheduleSearchRow, {passive: true});
      MOBILE_QUERY.addEventListener('change', updateSearchRow);
      mobileRow.hidden = false;
      updateSearchRow();
    }

    // Un tap sur la page voilée ferme tout, rangée mobile comprise.
    scrim.addEventListener('click', function () {
      if (rowOpen()) closeRow();
      else hide();
    });

    // Terme suggéré ou « Afficher tous les résultats » : même sortie que la touche
    // Entrée, avec l'événement `jaiio_search` (source instant_search).
    dropdown.addEventListener('click', function (event) {
      var termButton = event.target.closest('[data-jaiio-is-term]');
      if (!termButton) return;
      event.preventDefault();
      goToSearch(termButton.getAttribute('data-jaiio-is-term'));
    });

    document.addEventListener('click', function (event) {
      if (dropdown.hidden) return;
      if (dropdown.contains(event.target) || scrim === event.target) return;
      if (activeInput && (event.target === activeInput || activeInput.contains(event.target))) return;
      hide();
    });

    // Focus parti ailleurs que dans le champ, son formulaire (✨) ou le panneau —
    // tabulation, balayage d'un lecteur d'écran : le panneau se ferme.
    document.addEventListener('focusin', function (event) {
      if (dropdown.hidden) return;
      if (dropdown.contains(event.target)) return;
      if (activeInput && (event.target === activeInput || (activeInput.form && activeInput.form.contains(event.target)))) return;
      if (mobileRow && mobileRow.contains(event.target)) return;
      hide();
    });

    // Échap : d'abord le panneau (le focus revient au champ), puis la rangée mobile
    // (le focus revient à la loupe).
    document.addEventListener('keydown', function (event) {
      if (event.key !== 'Escape') return;
      if (!dropdown.hidden) {
        var inside = dropdown.contains(document.activeElement) || document.activeElement === activeInput;
        hide();
        if (inside && activeInput) {
          restoringInputFocus = true;
          activeInput.focus();
          restoringInputFocus = false;
        }
        return;
      }
      if (rowOpen()) closeRow();
    });

    window.addEventListener('resize', position, { passive: true });
    window.addEventListener('scroll', position, { passive: true });
  }
})();
